import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useCourse } from "api/hooks";
import { HeaderWithBreadcrumbs } from "components/molecules/HeaderWithBreadcrumbs/HeaderWithBreadbrumbs";

import { courseResourcesApi } from "api";
import { TabWithFolders, useCourseResources } from "api/course-resources";
import { AdminResourceDeleteModal, PageMain, ToggleReadinessButton } from "components/molecules";
import { routes } from "consts";
import { QUERIES_KEYS } from "queries";
import { useInavalidate, useToaster } from "utils";
import { PaziaLoader, PaziaWarningInfo } from "components/atoms";
import { Switch } from "components/atoms/PaziaSwitch/Switch";
import { DraggableReosurceList } from "./DraggableReosurceList";
import { ResourcesFoldersList } from "./ResourcesFoldersList";
import { AddCourseResourceTabModal } from "./TabModals/AddCourseResourceTabModal";
import { ResourceTabFormValues } from "./TabModals/ResourceTabForm";
import { PaziaBox } from "components/atoms";
import { coursesStatusApi } from "api/coursesStatusApi";

export const CourseResourcesPage = () => {
  const { courseId } = useParams();
  const [isTabModalOpen, setIsTabModalOpen] = useState(false);
  const { loading: loadingCourse, data: course, invalidate } = useCourse(Number(courseId));
  const { showError, showSuccess } = useToaster();
  const invalidateResources = useInavalidate(QUERIES_KEYS.COURSE_RESOURCES(Number(courseId)));

  const { isLoading, tabs } = useCourseResources(Number(courseId));
  const [activeTab, setActiveTab] = useState<TabWithFolders | null>(null);
  const [savingPaymentPolicy, setSavingPaymentPolicy] = useState(false);

  useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      setActiveTab(tabs[0]);
    }
  }, [tabs]);

  if (loadingCourse || isLoading) return <PaziaLoader position="center" />;
  if (!course) return null;

  const onRequireFullPaymentChange = async (requireFullPayment: boolean) => {
    setSavingPaymentPolicy(true);
    try {
      await courseResourcesApi.updatePaymentPolicy(Number(courseId), requireFullPayment);
      await invalidate();
      showSuccess("Zaktualizowano ustawienia dostępu do materiałów");
    } catch (error) {
      console.error(error);
      showError("Nie udało się zaktualizować ustawień dostępu do materiałów");
    } finally {
      setSavingPaymentPolicy(false);
    }
  };

  const onTabCreated = async (formData: ResourceTabFormValues) => {
    try {
      await courseResourcesApi.createTab(Number(courseId), formData);
      showSuccess("Zakładka została dodana");
      setIsTabModalOpen(false);
      invalidateResources();
    } catch (error) {
      console.error(error);
      showError("Nie udało się dodać zakładki");
    }
  };

  return (
    <PageMain>
      <HeaderWithBreadcrumbs
        breadcrumbs={[
          { title: "Kursy", path: routes.COURSES },
          {
            title: [course.data.name, course.data.variant].filter(Boolean).join(" · "),
            path: routes.COURSES_MANAGE(course.data.id),
          },
          { title: "Strefa studenta" },
        ]}
        headerText="Strefa studenta"
      />
      <PaziaBox className="mb-4">
        <ToggleReadinessButton
          isReady={Boolean(course?.data.resourcesReady)}
          onReady={coursesStatusApi.RESOURCES_READY}
          onUnready={coursesStatusApi.RESOURCES_UNREADY}
          courseId={Number(courseId)}
          readyText="Materiały zostały zatwierdzone"
          invalidate={async () => {
            invalidateResources();
            invalidate();
          }}
        />
      </PaziaBox>

      <PaziaBox className="mb-4">
        <Switch
          name="requireFullPaymentForResources"
          label="Wymagaj pełnej płatności do odblokowania materiałów"
          checked={Boolean(course.data.requireFullPaymentForResources)}
          disabled={savingPaymentPolicy}
          onBlur={() => undefined}
          onChange={(e) => onRequireFullPaymentChange(Boolean(e.target.checked ?? e.target.value))}
        />
      </PaziaBox>

      <div className="flex w-full flex-col rounded-sm border border-slate-200 bg-white pl-2 pt-2 shadow-lg dark:border-slate-700 dark:bg-slate-800">
        <DraggableReosurceList
          key={tabs.length}
          tabs={tabs}
          onAddTabClick={() => setIsTabModalOpen(true)}
          activeTabId={activeTab?.id}
          onTabClick={setActiveTab}
        />
        {activeTab?.id && <ResourcesFoldersList courseId={Number(courseId)} courseResourceTabId={activeTab.id} />}
      </div>

      <AddCourseResourceTabModal
        isOpen={isTabModalOpen}
        onCloseRequest={() => setIsTabModalOpen(false)}
        onSubmit={onTabCreated}
      />

      <AdminResourceDeleteModal
        title="Usuń zakładkę"
        description="Jesteś pewien że chcesz usunąć zakładkę?"
        onSuccessDelete={() => {}}
        deleteAction={async (x) => {
          await courseResourcesApi.deleteTab(Number(courseId), x);
          invalidateResources();
        }}
      />
    </PageMain>
  );
};
